import { View, Text, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { GuidePageHeader, GuideCard, GuideTip, GuideNav, getGuideNav } from "./_components";

const PAGE_HREF = "/(web)/guide/points";

/** Visual score example row */
const ScoreExample = ({ label, points, icon, color }: {
  label: string;
  points: string;
  icon: keyof typeof Ionicons.glyphMap;
  color: string;
}) => (
  <View className="flex-row items-center justify-between py-3 border-b border-gray-100">
    <View className="flex-row items-center gap-3">
      <Ionicons name={icon} size={18} color={color} />
      <Text className="text-sm text-gray-700">{label}</Text>
    </View>
    <View className="px-3 py-1 rounded-full" style={{ backgroundColor: color + "15" }}>
      <Text className="text-sm font-bold" style={{ color }}>{points}</Text>
    </View>
  </View>
);

export default function PointsPage() {
  if (Platform.OS !== "web") return null;
  const nav = getGuideNav(PAGE_HREF);

  return (
    <View>
      <GuidePageHeader
        icon="trophy"
        title="Punten"
        subtitle="Verdien punten voor je team door spellen te winnen, uitdagingen te voltooien en bonuspunten te verzamelen."
        color="#F59E0B"
      />

      {/* Hoe verdien je punten? */}
      <Text className="text-xl font-bold text-gray-900 mb-4">Hoe verdien je punten?</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Punten worden verdiend op verschillende manieren. Alle punten tellen mee voor de 
        totaalscore van je team.
      </Text>

      <View className="mb-6">
        <GuideCard
          icon="game-controller"
          title="Spellen winnen"
          description="Elk spel levert punten op. De exacte puntenwaarde verschilt per spel en wordt door de commissie ingesteld. Winnen levert meer op dan alleen deelnemen."
          color="#3B82F6"
        />
        <GuideCard
          icon="flash"
          title="Uitdagingen voltooien"
          description="Foto-opdrachten, quiz-uitdagingen en andere challenges leveren punten op wanneer je inzending wordt goedgekeurd."
          color="#F59E0B"
        />
        <GuideCard
          icon="star"
          title="Bonuspunten"
          description="De commissie kan handmatig bonuspunten toekennen voor speciale prestaties, samenwerking of sportiviteit."
          color="#8B5CF6"
        />
        <GuideCard
          icon="ribbon"
          title="Deelname-punten"
          description="Bij sommige spellen krijg je ook punten voor deelname, zelfs als je niet wint. Meedoen wordt beloond!"
          color="#10B981"
        />
      </View>

      {/* Voorbeeld punten */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Voorbeeld Puntenverdeling</Text>
      <Text className="text-base text-gray-600 leading-6 mb-4">
        De commissie stelt per spel en uitdaging in hoeveel punten er te verdienen zijn. 
        Hieronder een voorbeeld van hoe punten verdeeld kunnen worden:
      </Text>

      <View className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
        <ScoreExample label="Memory gewonnen" points="+50" icon="grid" color="#8B5CF6" />
        <ScoreExample label="Quiz voltooid" points="+30" icon="help-circle" color="#3B82F6" />
        <ScoreExample label="Foto-opdracht goedgekeurd" points="+40" icon="camera" color="#F59E0B" />
        <ScoreExample label="Speurtocht stap voltooid" points="+25" icon="map" color="#06B6D4" />
        <ScoreExample label="Live Quiz ronde gewonnen" points="+100" icon="tv" color="#EF4444" />
        <ScoreExample label="Bonuspunten (commissie)" points="+20" icon="star" color="#8B5CF6" />
      </View>

      <GuideTip variant="warning" icon="information-circle" title="Punten variëren">
        De bovenstaande punten zijn voorbeelden. De werkelijke puntenwaarde wordt per weekend 
        door de commissie ingesteld en kan per spel en moeilijkheidsgraad verschillen.
      </GuideTip>

      {/* Team vs Individueel */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Team vs Individueel</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Punten worden altijd bijgeschreven bij je team. Daarnaast kun je ook je individuele 
        bijdrage bekijken:
      </Text>

      <View className="mb-6">
        <GuideCard
          icon="people"
          title="Teamscore"
          description="De optelsom van alle punten die teamleden hebben verdiend. Dit bepaalt de teamstand op het leaderboard."
          color="#EC4899"
        />
        <GuideCard
          icon="person"
          title="Individuele score"
          description="Hoeveel punten jij persoonlijk hebt bijgedragen aan het team. Bekijk je score-uitsplitsing per spel en activiteit."
          color="#3B82F6"
        />
      </View>

      {/* Leaderboard */}
      <Text className="text-xl font-bold text-gray-900 mb-4 mt-8">Leaderboard</Text>
      <Text className="text-base text-gray-600 leading-6 mb-6">
        Het leaderboard toont de actuele teamstand. Je kunt zien:
      </Text>

      <View className="mb-6">
        <GuideCard
          icon="podium"
          title="Teamranglijst"
          description="Alle teams gerangschikt op totaalscore. Het team met de meeste punten staat bovenaan."
          color="#F59E0B"
        />
        <GuideCard
          icon="bar-chart"
          title="Score-uitsplitsing"
          description="Per team kun je zien hoe de punten zijn verdeeld: per spel, per uitdaging en per bonuspunt."
          color="#3B82F6"
        />
        <GuideCard
          icon="time"
          title="Scoregeschiedenis"
          description="Een tijdlijn van wanneer en waardoor punten zijn verdiend. Zo kun je de voortgang volgen."
          color="#10B981"
        />
      </View>

      <GuideTip variant="success" icon="trending-up" title="Real-time updates">
        Het leaderboard wordt in real-time bijgewerkt. Zodra er punten worden gescoord, 
        verandert de stand direct voor iedereen zichtbaar!
      </GuideTip>

      <GuideNav {...nav} />
    </View>
  );
}
